import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ currentPage, setCurrentPage, isOpen, onClose }) => {
  const { user } = useAuth();
  const [expandedSections, setExpandedSections] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const { isDarkMode } = useTheme();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Tableau de Bord',
      icon: 'fas fa-tachometer-alt',
      color: isDarkMode ? 'text-dark-primary-400' : 'text-blue-600'
    },
    {
      id: 'interventions',
      label: 'Interventions',
      icon: 'fas fa-tools',
      color: isDarkMode ? 'text-orange-400' : 'text-orange-600'
    },
    // Only show Clients if user is admin
    ...(user?.isAdmin ? [{
      id: 'clients',
      label: 'Clients',
      icon: 'fas fa-users',
      color: isDarkMode ? 'text-green-400' : 'text-green-600'
    }] : []),
    // Gestion Articles category: Articles always, Famille Type Article and Type Article only for admin
    {
      category: 'Gestion Articles',
      categoryId: 'gestion-articles',
      categoryIcon: 'fas fa-boxes',
      items: [
        {
          id: 'articles',
          label: 'Articles',
          icon: 'fas fa-fire-extinguisher',
          color: isDarkMode ? 'text-red-400' : 'text-red-600'
        },
        ...(user?.isAdmin ? [
          {
            id: 'famille-type-article',
            label: 'Famille Type Article',
            icon: 'fas fa-layer-group',
            color: isDarkMode ? 'text-purple-400' : 'text-purple-600'
          },
          {
            id: 'type-article',
            label: 'Type Article',
            icon: 'fas fa-tags',
            color: isDarkMode ? 'text-indigo-400' : 'text-indigo-600'
          }
        ] : [])
      ]
    },
    // Only show Pièces de rechange for admin
    ...(user?.isAdmin ? [{
      id: 'pieces-rechange',
      label: 'Pièces de rechange et service',
      icon: 'fas fa-cogs',
      color: isDarkMode ? 'text-amber-400' : 'text-amber-600'
    }] : []),
    {
      id: 'certificats',
      label: 'Certificats',
      icon: 'fas fa-certificate',
      color: isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
    },
    {
      id: 'locaux',
      label: 'Locaux',
      icon: 'fas fa-building',
      color: isDarkMode ? 'text-slate-400' : 'text-slate-600'
    }
  ];

  const handleItemClick = (itemId) => {
    setCurrentPage(itemId);
    // Close mobile sidebar when item is clicked
    if (isMobile) {
      onClose?.();
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  if (!isOpen && isMobile) return null;

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Fixed width of 288px (w-72) */}
      <aside className={`
        fixed top-0 left-0 h-full w-72 z-50
        lg:z-30
        bg-white dark:bg-gray-800 shadow-2xl lg:shadow-xl border-r border-gray-100 dark:border-gray-700
        transform transition-all duration-300 ease-out
        ${isOpen || !isMobile ? 'translate-x-0' : '-translate-x-full'}
        flex flex-col
      `}>

        {/* Header with Logo - Enhanced */}
        <div className={`relative px-6 py-4 border-b transition-colors ${
          isDarkMode 
            ? 'bg-gray-800 border-gray-700' // Use sidebar bg and border in dark mode
            : 'bg-gradient-to-r from-primary-600 to-primary-700 border-primary-500/20'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Logo Container */}
              <div className="relative w-16 h-16 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-0.5 flex items-center justify-center">
                <img
                  src="/assets/images/logo.png"
                  alt="Général Sécurité"
                  className="w-full h-full object-cover filter drop-shadow-sm scale-150"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                {/* Fallback with dynamic theming */}
                <div
                  className={`hidden w-full h-full items-center justify-center rounded-lg text-white font-bold text-lg shadow-inner ${
                    isDarkMode 
                      ? 'bg-gray-800' // Match sidebar bg in dark mode
                      : 'bg-gradient-to-br from-primary-500 to-primary-600'
                  }`}
                  style={{ display: 'none' }}
                >
                  GS
                </div>
              </div>

              {/* Company Name */}
              <div className="flex flex-col">
                <h1 className="text-white font-bold text-lg leading-tight">
                  Général Sécurité
                </h1>
                <p className={`text-xs opacity-90 ${
                  isDarkMode ? 'text-dark-primary-100' : 'text-primary-100'
                }`}>
                  Gestion & Sécurité
                </p>
              </div>
            </div>

            {/* Close Button - Mobile Only */}
            {isMobile && (
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors backdrop-blur-sm"
              >
                <i className="fas fa-times text-white text-sm"></i>
              </button>
            )}
          </div>

          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-white/5 to-transparent pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
        </div>

        {/* Navigation - Enhanced */}
        <nav className={`flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent px-4 py-6 transition-colors ${
          isDarkMode 
            ? 'scrollbar-thumb-gray-600 bg-gradient-to-b from-gray-800/50 to-gray-800' 
            : 'scrollbar-thumb-gray-300 bg-gradient-to-b from-gray-50/50 to-white'
        }`}>
          <div className="space-y-2">
            {menuItems.map((item, index) => {
              if (item.category) {
                const isExpanded = expandedSections[item.categoryId];
                const hasActiveSubItem = item.items.some(subItem => currentPage === subItem.id);

                return (
                  <div key={index} className="space-y-1">
                    {/* Category Header */}
                    <button
                      onClick={() => toggleSection(item.categoryId)}
                      className={`group w-full flex items-center justify-between px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                        hasActiveSubItem || isExpanded
                          ? isDarkMode
                            ? 'bg-dark-primary-900/50 text-dark-primary-300 shadow-sm ring-1 ring-dark-primary-700'
                            : 'bg-primary-50 text-primary-700 shadow-sm ring-1 ring-primary-100'
                          : isDarkMode
                            ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                          hasActiveSubItem || isExpanded
                            ? isDarkMode
                              ? 'bg-dark-primary-800 text-dark-primary-300'
                              : 'bg-primary-100 text-primary-600'
                            : isDarkMode
                              ? 'bg-gray-700 text-gray-400 group-hover:bg-gray-600'
                              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                        }`}>
                          <i className={`${item.categoryIcon} text-sm`}></i>
                        </div>
                        <span className="font-medium">{item.category}</span>
                      </div>
                      <i className={`fas fa-chevron-down transition-transform duration-200 text-xs ${isExpanded ? 'rotate-180' : ''}`}></i>
                    </button>

                    {/* Submenu with better animation */}
                    <div className={`ml-11 space-y-1 overflow-hidden transition-all duration-300 ease-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                      {item.items.map((subItem) => (
                        <button
                          key={subItem.id}
                          onClick={() => handleItemClick(subItem.id)}
                          className={`group w-full flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                            currentPage === subItem.id
                              ? isDarkMode
                                ? 'bg-dark-primary-600 text-white shadow-lg shadow-dark-primary-600/25 scale-[1.02] border-l-4 border-dark-primary-300'
                                : 'bg-primary-500 text-white shadow-lg shadow-primary-500/25 scale-[1.02] border-l-4 border-primary-300'
                              : isDarkMode
                                ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200 hover:translate-x-1'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:translate-x-1'
                          }`}
                        >
                          <div className={`w-6 h-6 rounded-md flex items-center justify-center mr-3 transition-colors ${
                            currentPage === subItem.id
                              ? 'bg-white/20 text-white'
                              : isDarkMode
                                ? 'text-gray-500 group-hover:text-gray-300'
                                : 'text-gray-400 group-hover:text-gray-600'
                          }`}>
                            <i className={`${subItem.icon} text-xs`}></i>
                          </div>
                          <span className="truncate">{subItem.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }

              // Regular menu items
              return (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item.id)}
                  className={`group w-full flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                    currentPage === item.id
                      ? isDarkMode
                        ? 'bg-dark-primary-600 text-white shadow-lg shadow-dark-primary-600/25 scale-[1.02]'
                        : 'bg-primary-500 text-white shadow-lg shadow-primary-500/25 scale-[1.02]'
                      : isDarkMode
                        ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100 hover:translate-x-1'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:translate-x-1'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 transition-colors ${
                    currentPage === item.id
                      ? 'bg-white/20 text-white'
                      : isDarkMode
                        ? `bg-gray-700 ${item.color} group-hover:bg-gray-600 group-hover:scale-110`
                        : `bg-gray-100 ${item.color} group-hover:bg-gray-200 group-hover:scale-110`
                  }`}>
                    <i className={`${item.icon} text-sm`}></i>
                  </div>
                  <span className="truncate font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Footer - Enhanced */}
        <div className={`border-t p-4 transition-colors ${
          isDarkMode 
            ? 'border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900' 
            : 'border-gray-100 bg-gradient-to-r from-gray-50 to-white'
        }`}>
          <div className="flex items-center justify-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
            <i className={`fas fa-shield-alt ${isDarkMode ? 'text-dark-primary-400' : 'text-primary-500'}`}></i>
            <span>Sécurisé & Fiable</span>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;